export const BillingPlanIds = Object.freeze({
  Trial: 0,
  StampsPlan: 100,
  UpsCtpLight: 15,
  UpsCtpSmall: 16,
  UpsCtpMedium: 17,
  UpsCtpLarge: 18,
  SquareStarter: 21,
  StampsDesktop: 22,
  Starter2019: 23,
  YahooFree: 24,
  YahooStarter: 25,
  YahooBronze: 26,
  YahooSilver: 27,
  FreePostal: 30,
  FreeStarter: 31,
  SSEnterpriseUK: 42,
  SSStartupAU: 44,
  SSAccelerateAU: 45,
  SSScaleAU: 46,
  SSEnterpriseAU: 47,
  Starter2022Q2: 52,
  Starter2022Q3Grandfathered: 59,
  Starter2022Q3: 66,
  Starter2022Q3NonWalleted: 166,
  Bronze2022Q3NonWalleted: 167,
  Silver2022Q3NonWalleted: 168,
  Gold2022Q3NonWalleted: 169,
  Platinum2022Q3NonWalleted: 170,
  Enterprise2022Q4NonWalleted: 173,
  HighVolume2022Q3PlanId: 72,
  HighVolume2023Q2PlanId: 74,
  HighVolume2024_4PlanId: 75,
  HighVolume2024_45PlanId: 76,
  HighVolume2024_5PlanId: 77,
  HighVolume2024_35PlanId: 78,
  Bronze2022Q3: 67,
  Silver2022Q3: 68,
  Gold2022Q3: 69,
  Platinum2022Q3: 70,
  Enterprise2022Q4: 73,
  SSHighVolumeANZQ22023: 93,
  SSStartupANZQ12023: 94,
  SSAccelerateANZQ12023: 95,
  SSScaleANZQ12023: 96,
  SSEnterpriseANZQ12023: 97,
  SSUKEnterprise2023Q2: 112,
  SSEUStartupBYOA2023Q2: 113,
  SSEUAccelerateBYOA2023Q2: 114,
  SSEUScaleBYOA2023Q2: 115,
  SSUKStartupBYOA2023Q2: 117,
  SSUKAccelerateBYOA2023Q2: 118,
  SSUKScaleBYOA2023Q2: 119,
  Free2025Q2: 120,
  Starter2025Q2: 121,
  Standard2025Q2: 122,
  Premium2025Q2: 123,
  Starter2025Q2Annual: 124,
  Standard2025Q2Annual: 125,
  Premium2025Q2Annual: 126,
  UpsDcBasic: 128,
  UpsDcIntermediate: 129,
  UpsDcAdvanced: 130,
  UpsDcEnterprise: 131,
  Starter50Migration: 175,
  Starter100Migration: 176,
  Starter500Migration: 177,
  Starter1000Migration: 178,
  Starter2000Migration: 179,
  Starter5000Migration: 180,
  Standard50Migration: 181,
  Standard100Migration: 182,
  Standard500Migration: 183,
  Standard1000Migration: 184,
  Standard2000Migration: 185,
  Standard5000Migration: 186,
  Standard7500Migration: 187,
  Standard10000Migration: 188,
  Standard15000Migration: 189,
  Standard20000Migration: 190,
  Standard30000Migration: 191,
  Standard40000Migration: 192,
  Standard50000Migration: 193,
  Standard100000Migration: 194,
  Premium50Migration: 195,
  Premium100Migration: 196,
  Premium500Migration: 197,
  Premium1000Migration: 198,
  Premium2000Migration: 199,
  Premium5000Migration: 200,
  Premium7500Migration: 201,
  Premium10000Migration: 202,
  Premium15000Migration: 203,
  Premium20000Migration: 204,
  Premium30000Migration: 205,
  Premium40000Migration: 206,
  Premium50000Migration: 207,
  Premium100000Migration: 208,
} as const);

export type BillingPlanId = (typeof BillingPlanIds)[keyof typeof BillingPlanIds];

const UsageBilledHighVolumeBillingPlanIds: ReadonlyArray<number> = Object.freeze([
  BillingPlanIds.HighVolume2023Q2PlanId,
  BillingPlanIds.HighVolume2024_4PlanId,
  BillingPlanIds.HighVolume2024_45PlanId,
  BillingPlanIds.HighVolume2024_5PlanId,
  BillingPlanIds.HighVolume2024_35PlanId,
]);

export const isBillingPlanIdUsageBilledHighVolume = (billingPlanId: string | number): boolean => {
  const parsed = parseInt(String(billingPlanId), 10);
  return UsageBilledHighVolumeBillingPlanIds.includes(parsed);
};

export const HighVolumePlanToUsagePriceMap: Readonly<Record<number, number>> = Object.freeze({
  [BillingPlanIds.HighVolume2023Q2PlanId]: 0.08,
  [BillingPlanIds.HighVolume2024_4PlanId]: 0.04,
  [BillingPlanIds.HighVolume2024_45PlanId]: 0.045,
  [BillingPlanIds.HighVolume2024_5PlanId]: 0.05,
  [BillingPlanIds.HighVolume2024_35PlanId]: 0.035,
});

export const BillingPlansThatIncludeNonWalletedAccess: ReadonlyArray<number> = Object.freeze([
  BillingPlanIds.Starter2022Q3NonWalleted,
  BillingPlanIds.Bronze2022Q3NonWalleted,
  BillingPlanIds.Silver2022Q3NonWalleted,
  BillingPlanIds.Gold2022Q3NonWalleted,
  BillingPlanIds.Platinum2022Q3NonWalleted,
  BillingPlanIds.Enterprise2022Q4NonWalleted,
  BillingPlanIds.HighVolume2023Q2PlanId,
  BillingPlanIds.HighVolume2024_4PlanId,
  BillingPlanIds.HighVolume2024_45PlanId,
  BillingPlanIds.HighVolume2024_5PlanId,
  BillingPlanIds.HighVolume2024_35PlanId,
]);

export const BillingPlansThatDoNotIncludeNonWalletedAccess: ReadonlyArray<number> = Object.freeze([
  BillingPlanIds.Starter2022Q3,
  BillingPlanIds.Bronze2022Q3,
  BillingPlanIds.Silver2022Q3,
  BillingPlanIds.Gold2022Q3,
  BillingPlanIds.Platinum2022Q3,
  BillingPlanIds.Enterprise2022Q4,
]);

export const BillingPlanPerLabelCharges: Readonly<Record<number, number>> = Object.freeze({
  [BillingPlanIds.SSStartupANZQ12023]: 0.22,
  [BillingPlanIds.SSAccelerateANZQ12023]: 0.13,
  [BillingPlanIds.SSScaleANZQ12023]: 0.08,
  [BillingPlanIds.SSEnterpriseANZQ12023]: 0.05,
  [BillingPlanIds.SSHighVolumeANZQ22023]: 0.05,

  // EU
  [BillingPlanIds.SSEUStartupBYOA2023Q2]: 0.05,
  [BillingPlanIds.SSEUAccelerateBYOA2023Q2]: 0.04,
  [BillingPlanIds.SSEUScaleBYOA2023Q2]: 0.03,

  // UK
  [BillingPlanIds.SSUKStartupBYOA2023Q2]: 0.05,
  [BillingPlanIds.SSUKAccelerateBYOA2023Q2]: 0.04,
  [BillingPlanIds.SSUKScaleBYOA2023Q2]: 0.03,
});

export const NZ_AU_BILLING_PLAN_IDS: ReadonlyArray<number> = Object.freeze([
  BillingPlanIds.SSStartupAU,
  BillingPlanIds.SSAccelerateAU,
  BillingPlanIds.SSScaleAU,
  BillingPlanIds.SSEnterpriseAU,
  BillingPlanIds.SSHighVolumeANZQ22023,
  BillingPlanIds.SSStartupANZQ12023,
  BillingPlanIds.SSAccelerateANZQ12023,
  BillingPlanIds.SSScaleANZQ12023,
  BillingPlanIds.SSEnterpriseANZQ12023,
]);

export const BILLING_PLANS_UK_ENTERPRISE: ReadonlyArray<number> = Object.freeze([
  BillingPlanIds.SSEnterpriseUK,
  BillingPlanIds.SSUKEnterprise2023Q2,
]);

export const UPS_CTP_BILLING_PLAN_IDS: ReadonlyArray<number> = Object.freeze([
  BillingPlanIds.UpsCtpLight,
  BillingPlanIds.UpsCtpSmall,
  BillingPlanIds.UpsCtpMedium,
  BillingPlanIds.UpsCtpLarge,
]);

export const UPS_DC_BILLING_PLAN_IDS: ReadonlyArray<number> = Object.freeze([
  BillingPlanIds.UpsDcBasic,
  BillingPlanIds.UpsDcIntermediate,
  BillingPlanIds.UpsDcAdvanced,
  BillingPlanIds.UpsDcEnterprise,
]);

export const BILLING_PLANS_GOODBETTERBEST_2025_Q2: ReadonlyArray<number> = Object.freeze([
  BillingPlanIds.Free2025Q2,
  BillingPlanIds.Starter2025Q2,
  BillingPlanIds.Standard2025Q2,
  BillingPlanIds.Premium2025Q2,
  BillingPlanIds.Starter2025Q2Annual,
  BillingPlanIds.Standard2025Q2Annual,
  BillingPlanIds.Premium2025Q2Annual,
  BillingPlanIds.Trial,
  BillingPlanIds.Starter50Migration,
  BillingPlanIds.Starter100Migration,
  BillingPlanIds.Starter500Migration,
  BillingPlanIds.Starter1000Migration,
  BillingPlanIds.Starter2000Migration,
  BillingPlanIds.Starter5000Migration,
  BillingPlanIds.Standard50Migration,
  BillingPlanIds.Standard100Migration,
  BillingPlanIds.Standard500Migration,
  BillingPlanIds.Standard1000Migration,
  BillingPlanIds.Standard2000Migration,
  BillingPlanIds.Standard5000Migration,
  BillingPlanIds.Standard7500Migration,
  BillingPlanIds.Standard10000Migration,
  BillingPlanIds.Standard15000Migration,
  BillingPlanIds.Standard20000Migration,
  BillingPlanIds.Standard30000Migration,
  BillingPlanIds.Standard40000Migration,
  BillingPlanIds.Standard50000Migration,
  BillingPlanIds.Standard100000Migration,
  BillingPlanIds.Premium50Migration,
  BillingPlanIds.Premium100Migration,
  BillingPlanIds.Premium500Migration,
  BillingPlanIds.Premium1000Migration,
  BillingPlanIds.Premium2000Migration,
  BillingPlanIds.Premium5000Migration,
  BillingPlanIds.Premium7500Migration,
  BillingPlanIds.Premium10000Migration,
  BillingPlanIds.Premium15000Migration,
  BillingPlanIds.Premium20000Migration,
  BillingPlanIds.Premium30000Migration,
  BillingPlanIds.Premium40000Migration,
  BillingPlanIds.Premium50000Migration,
  BillingPlanIds.Premium100000Migration,
]);

const STARTER_PLAN_IDS: ReadonlySet<number> = new Set([
  BillingPlanIds.Starter2025Q2,
  BillingPlanIds.Starter2025Q2Annual,
  BillingPlanIds.Starter50Migration,
  BillingPlanIds.Starter100Migration,
  BillingPlanIds.Starter500Migration,
  BillingPlanIds.Starter1000Migration,
  BillingPlanIds.Starter2000Migration,
  BillingPlanIds.Starter5000Migration,
]);

const STANDARD_PLAN_IDS: ReadonlySet<number> = new Set([
  BillingPlanIds.Standard2025Q2,
  BillingPlanIds.Standard2025Q2Annual,
  BillingPlanIds.Standard50Migration,
  BillingPlanIds.Standard100Migration,
  BillingPlanIds.Standard500Migration,
  BillingPlanIds.Standard1000Migration,
  BillingPlanIds.Standard2000Migration,
  BillingPlanIds.Standard5000Migration,
  BillingPlanIds.Standard7500Migration,
  BillingPlanIds.Standard10000Migration,
  BillingPlanIds.Standard15000Migration,
  BillingPlanIds.Standard20000Migration,
  BillingPlanIds.Standard30000Migration,
  BillingPlanIds.Standard40000Migration,
  BillingPlanIds.Standard50000Migration,
  BillingPlanIds.Standard100000Migration,
]);

const PREMIUM_PLAN_IDS: ReadonlySet<number> = new Set([
  BillingPlanIds.Premium2025Q2,
  BillingPlanIds.Premium2025Q2Annual,
  BillingPlanIds.Premium50Migration,
  BillingPlanIds.Premium100Migration,
  BillingPlanIds.Premium500Migration,
  BillingPlanIds.Premium1000Migration,
  BillingPlanIds.Premium2000Migration,
  BillingPlanIds.Premium5000Migration,
  BillingPlanIds.Premium7500Migration,
  BillingPlanIds.Premium10000Migration,
  BillingPlanIds.Premium15000Migration,
  BillingPlanIds.Premium20000Migration,
  BillingPlanIds.Premium30000Migration,
  BillingPlanIds.Premium40000Migration,
  BillingPlanIds.Premium50000Migration,
  BillingPlanIds.Premium100000Migration,
]);

const parsePlanId = (billingPlanId: string | number): number =>
  typeof billingPlanId === 'number' ? billingPlanId : parseInt(billingPlanId, 10);

export const isStarterPlan = (billingPlanId: string | number | null | undefined): boolean => {
  if (billingPlanId == null) return false;
  return STARTER_PLAN_IDS.has(parsePlanId(billingPlanId));
};

export const isStandardPlan = (billingPlanId: string | number | null | undefined): boolean => {
  if (billingPlanId == null) return false;
  return STANDARD_PLAN_IDS.has(parsePlanId(billingPlanId));
};

export const isPremiumPlan = (billingPlanId: string | number | null | undefined): boolean => {
  if (billingPlanId == null) return false;
  return PREMIUM_PLAN_IDS.has(parsePlanId(billingPlanId));
};

export const isNewLineupPlan = (billingPlanId: string | number | null | undefined): boolean => {
  if (billingPlanId == null) return false;
  return isStarterPlan(billingPlanId) || isStandardPlan(billingPlanId) || isPremiumPlan(billingPlanId);
};

export const isXMigrationPlanCode = (code: string | null | undefined) => code?.endsWith('_migration');
